import axios, { AxiosError, AxiosResponse } from "axios";

import { PublicClientApplication } from "@azure/msal-browser";
import {
  apiURL,
  basePath,
  corporateApiUrl,
  corporateHost,
  eramanApiURL,
  eramanHost,
  magpApiURL,
  magpHost,
  passengerApiUrl,
  passengerHost,
  xApiKey,
} from "@utils/Constants";
import { strings } from "@utils/Strings";
import { loginRequest, msalConfig } from "@utils/azure-ad-sso";
import { message as notificationMessage } from "antd";
import { get as _get, isEmpty } from "lodash";

export const apiBaseUrl = `${apiURL}/${basePath}`;

export const msalInstance = new PublicClientApplication(msalConfig);
const ignoreErrorEndpoints = [
  "v1/application/license/unmapped/count",
  "v1/clm/envelope/notification",
  "v1/clm/envelope/",
];

const validateResponse = (res: AxiosResponse, resolve: Function, reject: Function, endpoint?: any) => {
  const { data } = res;

  if ((data && data.status === "OK") || data.status === "CREATED") {
    resolve(data);
  } else {
    if (data.message == "ConsoleView URL Fetch Failed") {
      reject(data);
    }
    if (endpoint && !ignoreErrorEndpoints.includes(endpoint)) {
      notificationMessage.error(data.message);
    }
    reject(data.message);
  }
};

const extractErrMsg = (err: AxiosError, reject: Function, endpoint?: any) => {
  if (err) {
    if (err.code == "ERR_NETWORK") {
      reject(strings.someThingWentWrong);
    }
    const { message } = err?.response?.data as any;
    if (message) {
      if (endpoint && !ignoreErrorEndpoints.includes(endpoint)) {
        notificationMessage.error(message);
      }
      reject(message);
      return;
    }
    if (endpoint && !ignoreErrorEndpoints.includes(endpoint)) {
      notificationMessage.error(strings.someThingWentWrong);
    }
    reject(strings.someThingWentWrong);
  }
};

const handleErr = (err: AxiosError, reject: Function, endpoint?: string) => {
  // if (err.response?.status === 401) {
  //   localStorage.removeItem("user");
  //   window.location.reload();
  // }
  return extractErrMsg(err, reject, endpoint);
};

export const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  headers: {
    "x-api-key": xApiKey,
    // "X-Auth-Type": "manual", - not required
  },
});

export const getPasswordApi = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  headers: {
    "x-api-key": xApiKey,
    "X-Auth-Type": "manual",
  },
});

export const eramanBEApi = axios.create({
  baseURL: eramanApiURL,
  withCredentials: true,
  headers: {
    "x-api-key": xApiKey,
    // "X-Auth-Type": "manual", - not required
  },
});

eramanBEApi.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const authProvider = JSON.parse(localStorage.getItem("authProvider") || '"internal"');

    if (!isEmpty(user)) {
      config.headers["X-Auth-Provider"] = authProvider;
    } else {
      delete config.headers["X-Auth-Provider"];
      delete config.headers["Authorization"];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const eramanCMSApi = axios.create({
  baseURL: eramanHost,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

export const corporateCMSApi = axios.create({
  baseURL: corporateHost,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

export const corporateBEApi = axios.create({
  baseURL: corporateApiUrl,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

export const corporateBEApiButterflyEffect = axios.create({
  baseURL: corporateApiUrl,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

corporateBEApiButterflyEffect.interceptors.request.use(
  async (config) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const account = msalInstance.getAllAccounts()[0];

      config.headers.Email = user?.email || "";

      if (account) {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account,
        });

        if (tokenResponse?.accessToken) {
          config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        }
      }
    } catch (err) {
      console.error("Failed to attach Authorization token", err);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

export const corporateBEApiForReviewForm = axios.create({
  baseURL: corporateApiUrl,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

corporateBEApiForReviewForm.interceptors.request.use(
  async (config) => {
    try {
      const account = msalInstance.getAllAccounts()[0];

      if (account) {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account,
        });

        if (tokenResponse?.accessToken) {
          config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        }
      }
    } catch (err) {
      console.error("Failed to attach Authorization token", err);
    }

    return config;
  },
  (error) => Promise.reject(error)
);
export const magpBEApi = axios.create({
  baseURL: magpApiURL,
  withCredentials: false,
  headers: {
    "APIM-token": JSON.parse(localStorage.getItem("apim"))?.access_token || "",
  },
});

export const magpCMSApi = axios.create({
  baseURL: magpHost,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

export const passengerCMSApi = axios.create({
  baseURL: passengerHost,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

export const passengerBEApi = axios.create({
  baseURL: passengerApiUrl,
  withCredentials: false,
  headers: {
    "x-api-key": xApiKey,
  },
});

let isRefreshing = false;
let refreshQueue: any = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve: any, reject: any) => {
          refreshQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user && user.role) {
          // Azure AD Flow
          const accessTokenRequest = {
            scopes: loginRequest.scopes,
            account: msalInstance.getAllAccounts()[0],
          };
          let res: any = await msalInstance.acquireTokenSilent(accessTokenRequest);
          if (res && res.accessToken) {
            // Call Backend with new Access Token to set Cookies logging out test
            await axios.post(
              `${apiBaseUrl}/user/refresh-cookie`,
              {
                token: res.accessToken,
              },
              { withCredentials: true }
            );
          } else {
            logout();
          }
        } else {
          // const payload = {
          //   refreshToken: user?.refreshToken,
          // };
          // await axios.post(`${apiBaseUrl}/userprofile/refresh/token`, payload, {
          //   withCredentials: true
          // });
          const refreshReq = await axios.post(`${apiBaseUrl}/userprofile/refresh/token`, {
            refreshToken: JSON.parse(localStorage.getItem("user") || "{}")?.refreshToken,
          });
          const { token, refreshToken } = _get(refreshReq, "data.response.data");
          let accessToken = token;
          if (token) {
            // Call Backend with new Access Token to set Cookies
            await axios.post(
              `${apiBaseUrl}/user/refresh-cookie`,
              {
                token: accessToken,
              },
              { withCredentials: true }
            );
          } else {
            logout();
          }
        }

        refreshQueue.forEach((prom: any) => {
          prom.resolve();
        });
        refreshQueue = [];
        isRefreshing = false;

        // Retry original request (browser handles new cookie)
        return api(originalRequest);
      } catch (error) {
        logout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const logout = async () => {
  try {
    updateHeader();
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    // Payload for logout API
    // const payload = {
    //   email: user?.email,
    //   // Add other necessary fields
    // };
    await axios.post(`${apiBaseUrl}/user/logout`, {}, { withCredentials: true });
  } catch (e) {
    console.error("Logout API failed", e);
  }

  localStorage.clear();
  sessionStorage.clear();
  window.localStorage.removeItem("user");
  window.localStorage.removeItem("redirectTo");

  // MSAL Logout cleanup
  if (msalInstance.getActiveAccount()) {
    msalInstance.logoutPopup({ account: msalInstance.getActiveAccount() });
  }

  const queryParams = new URLSearchParams(location.search);
  const redirectTo: any = queryParams.get("redirect") || "";
  window.history.pushState({ sessionExpired: true }, "", "/auth/login?redirect=" + redirectTo);
  window.location.reload();
};

export const silentRefresh = async () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user && user.role) {
      // Azure AD Flow
      const accessTokenRequest = {
        scopes: loginRequest.scopes,
        account: msalInstance.getAllAccounts()[0],
      };
      let res: any = await msalInstance.acquireTokenSilent(accessTokenRequest);
      if (res && res.accessToken) {
        // Call Backend with new Access Token to set Cookies
        await axios.post(
          `${apiBaseUrl}/user/refresh-cookie`,
          {
            token: res.accessToken,
          },
          { withCredentials: true }
        );
      } else {
        logout();
      }
    } else {
      logout();
    }
  } catch (error) {
    console.error("Silent refresh failed", error);
    logout();
  }
};

const updateHeader = () => {
  const user = JSON.parse(localStorage.getItem("user") as any) || {};
  const authProvider = JSON.parse(localStorage.getItem("authProvider") as any);
  if (!isEmpty(user)) {
    // const { token } = user;
    // Cookies are used now, no Bearer token needed
    // api.defaults.headers.common["Authorization"] = `Bearer ${token} `;
    api.defaults.headers.common["X-Auth-Provider"] = authProvider || "internal";
    return;
  }
  api.defaults.headers.common["Authorization"] = "";
};

const updateEramanHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const authProvider = JSON.parse(localStorage.getItem("authProvider") || '"internal"');

  if (!isEmpty(user)) {
    // const { token } = user;

    // eramanBEApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    eramanBEApi.defaults.headers.common["X-Auth-Provider"] = authProvider;

    // eramanCMSApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    eramanCMSApi.defaults.headers.common["X-Auth-Provider"] = authProvider;
  } else {
    eramanBEApi.defaults.headers.common["Authorization"] = "";
    eramanCMSApi.defaults.headers.common["Authorization"] = "";
  }
};

export const get = async (endpoint: string) => {
  updateHeader();
  return new Promise((resolve: any, reject: any) => {
    api
      .get(endpoint)
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

export const post = async (
  endpoint: string,
  // eslint-disable-next-line
  payload: any,
  headers?: { [key in string]: any },
  disableAuth?: boolean
) => {
  if (!disableAuth) {
    updateHeader();
  }
  return new Promise((resolve: any, reject: any) => {
    api
      .post(endpoint, payload, {
        ...(headers && {
          headers: {
            ...headers,
          },
        }),
      })
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject, endpoint);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject, endpoint);
      });
  });
};

// eslint-disable-next-line
export const put = async (endpoint: string, payload: any) => {
  updateHeader();
  return new Promise((resolve: any, reject: any) => {
    api
      .put(endpoint, payload)
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

// eslint-disable-next-line
export const remove = async (endpoint: string, payload?: any) => {
  updateHeader();
  return new Promise((resolve: any, reject: any) => {
    api
      .delete(endpoint, {
        ...(payload && { data: payload }),
      })
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

export const upload = (endpoint: string, payload: any) => {
  return new Promise((resolve: any, reject: any) => {
    const user = JSON.parse(localStorage.getItem("user") as any) || {};
    if (!user) reject("Unauthorized User");
    // const myHeaders = new Headers();
    // myHeaders.append("Authorization", `Bearer ${user.token}`);

    fetch(`${apiURL}/${basePath}/${endpoint}`, {
      method: "POST",
      body: payload,
      // headers: myHeaders, // Cookies handled by browser
      credentials: "include", // Ensure cookies are sent
      redirect: "follow",
    })
      .then((response) => response.json())
      .then((res: any) => {
        resolve(res);
      })
      .catch((err: AxiosError) => {
        reject(handleErr(err, reject));
      });
  });
};

export const getEramanBE = async (endpoint: string) => {
  updateEramanHeaders();
  return new Promise((resolve: any, reject: any) => {
    eramanBEApi
      .get(endpoint)
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

export const postEramanBE = async (
  endpoint: string,
  // eslint-disable-next-line
  payload: any,
  headers?: { [key in string]: any }
) => {
  updateEramanHeaders();
  return new Promise((resolve: any, reject: any) => {
    eramanBEApi
      .post(endpoint, payload, {
        ...(headers && {
          headers: {
            ...headers,
          },
        }),
      })
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject, endpoint);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject, endpoint);
      });
  });
};

// eslint-disable-next-line
export const putEramanBE = async (endpoint: string, payload: any) => {
  updateEramanHeaders();
  return new Promise((resolve: any, reject: any) => {
    eramanBEApi
      .put(endpoint, payload)
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

// eslint-disable-next-line
export const removeEramanBE = async (endpoint: string, payload?: any) => {
  updateEramanHeaders();
  return new Promise((resolve: any, reject: any) => {
    eramanBEApi
      .delete(endpoint, {
        ...(payload && { data: payload }),
      })
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};
